import { useState, useEffect, useRef, useCallback } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { lookupUserByHandle } from '../../services/inviteService'

export interface HandleSearchProps {
  onSelect: (data: { handle: string; userId: string }) => void
}

const DEBOUNCE_DELAY = 500

const HandleSearch = ({ onSelect }: HandleSearchProps) => {
  const [inputValue, setInputValue] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [foundUser, setFoundUser] = useState<{
    displayName: string
    handle: string
    userId: string
  } | null>(null)
  const [notFound, setNotFound] = useState(false)
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const resetResults = useCallback(() => {
    setFoundUser(null)
    setNotFound(false)
  }, [])

  useEffect(() => {
    // Clear previous debounce
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }

    const trimmed = inputValue.trim()

    // Reset states when input is empty
    if (!trimmed) {
      setIsSearching(false)
      resetResults()
      return
    }

    setIsSearching(true)
    resetResults()

    debounceTimer.current = setTimeout(async () => {
      try {
        const user = await lookupUserByHandle(trimmed)

        if (user) {
          setFoundUser({
            displayName: user.displayName,
            handle: user.handle ?? trimmed,
            userId: user.id
          })
          setNotFound(false)
        } else {
          setFoundUser(null)
          setNotFound(true)
        }
      } catch {
        setFoundUser(null)
        setNotFound(true)
      } finally {
        setIsSearching(false)
      }
    }, DEBOUNCE_DELAY)

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [inputValue, resetResults])

  const handleSelect = () => {
    if (foundUser) {
      onSelect({ handle: foundUser.handle, userId: foundUser.userId })
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        label='Handle'
        placeholder='Enter a user handle...'
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        fullWidth
        error={notFound}
        helperText={notFound ? 'User not found' : undefined}
        slotProps={{
          input: {
            endAdornment: isSearching ? (
              <CircularProgress color='inherit' size={20} />
            ) : null
          }
        }}
      />

      {foundUser && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            cursor: 'pointer'
          }}
          onClick={handleSelect}
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleSelect()
            }
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant='body1'>{foundUser.displayName}</Typography>
            <Typography variant='body2' color='text.secondary'>
              @{foundUser.handle}
            </Typography>
          </Box>
          <Chip label='Select' size='small' color='primary' clickable />
        </Box>
      )}
    </Box>
  )
}

export default HandleSearch
